// apps/admin/src/lib/rbac.ts
//
// Phase 8.1 Task 02 — RBAC helpers for the admin SPA.
//
// vg-binding: P8.D-163 — Layout-level auth guard + per-route permission
//             check (`getAllowedRoutes` powers the Cmd+K palette + sidebar
//             filtering in tasks 03/04).
//
// SEC-5: grants come ONLY from the backend `/auth/me/permissions` response.
// A role name grants nothing on the client — the backend resolves roles to
// permissions; matching uses the one matcher in `permission-match.ts`.
//
// Usage:
//   import { hasPermission, getAllowedRoutes } from '@/lib/rbac';
//   if (!hasPermission(user, perms, PERMISSIONS.MANAGE_VENDORS)) {…}
//
// Notes:
//   - `user` may be `null` (unauthenticated) — helpers return `false`.
//   - `permissions[]` from the backend is the SOURCE OF TRUTH at request
//     time. The FE catalog here is a UX hint (hide menu items, gray out
//     buttons). The backend `requirePermission` middleware re-checks.

import type { AuthUser } from '@printway/ui';

import { hasGrant } from './permission-match.js';
import { PERMISSIONS, type Permission } from './permissions.js';

// ---------------------------------------------------------------------------
// Route catalog — Phase 8.1 surface only (8.2/8.3 extend separately).
// Each entry maps a route path to the permission that gates it.
// Cmd+K palette (task-03 sibling) reads this through `getAllowedRoutes` to
// drop entries the user cannot reach.
// ---------------------------------------------------------------------------

export interface AdminRoute {
  /** SPA URL path, no query string. */
  path: string;
  /** Sidebar/Cmd-K label (i18n key); FE i18n resolver does the lookup. */
  label: string;
  /**
   * Permission required to even SEE the route. Use the most permissive of
   * the route's view permissions (mutation perms layer on top inside the
   * page).
   */
  permission: Permission;
}

/**
 * Phase 8.1 admin routes (flat). 8.2/8.3 inject their own
 * routes through the same shape from their respective `routes.ts`
 * modules; 8.1 owns this slice only.
 */
export const ADMIN_ROUTES_8_1: ReadonlyArray<AdminRoute> = [
  // Dashboard
  { path: '/dashboard', label: 'nav.dashboard', permission: PERMISSIONS.VIEW_DASHBOARD },
  // Orders (list + cost-aware detail variant)
  { path: '/orders', label: 'nav.orders', permission: PERMISSIONS.VIEW_ORDERS },
  { path: '/orders/:orderId', label: 'nav.order_detail', permission: PERMISSIONS.VIEW_ORDER_DETAIL },
  // Retired (batch E follow-up): '/order-assign-rules' — page + BE surface gone.
  // Vendors
  { path: '/vendors', label: 'nav.vendors', permission: PERMISSIONS.VIEW_VENDORS },
  { path: '/vendors/:vendorId', label: 'nav.vendor_detail', permission: PERMISSIONS.VIEW_VENDORS },
  // Merchants
  { path: '/merchants', label: 'nav.merchants', permission: PERMISSIONS.VIEW_MERCHANTS },
  { path: '/merchants/:merchantId', label: 'nav.merchant_detail', permission: PERMISSIONS.VIEW_MERCHANTS },
  // Stores
  { path: '/stores', label: 'nav.stores', permission: PERMISSIONS.VIEW_STORES },
  { path: '/shipment-tracking', label: 'nav.shipment_tracking', permission: PERMISSIONS.VIEW_ORDERS },
  // Profile / settings (always viewable when authenticated)
  { path: '/profile', label: 'nav.profile', permission: PERMISSIONS.VIEW_DASHBOARD },
];

// ---------------------------------------------------------------------------
// Permission resolution helpers
// ---------------------------------------------------------------------------

/**
 * True iff the signed-in user holds a backend grant covering `key`.
 * `permissions` is the `/auth/me/permissions` list; wildcards follow the
 * backend rules (see `permission-match.ts`).
 */
export function hasPermission(
  user: AuthUser | null,
  permissions: ReadonlyArray<string> | undefined,
  key: Permission | string,
): boolean {
  return user != null && hasGrant(permissions, key);
}

/**
 * Filters the route catalog to those the user can reach. Consumed by
 * Cmd+K palette (task-03 sibling) + sidebar disabled-state computation.
 */
export function getAllowedRoutes(
  user: AuthUser | null,
  permissions: ReadonlyArray<string> | undefined,
  catalog: ReadonlyArray<AdminRoute> = ADMIN_ROUTES_8_1,
): ReadonlyArray<AdminRoute> {
  if (!user) return [];
  return catalog.filter((route) =>
    hasPermission(user, permissions, route.permission),
  );
}
